<template>
  <div class="w-full">
    <fg-input v-if="showClientAutocomplete"
              :label="$t('generic.client')"
              :error="getError($t('generic.client'))"
              required>
      <client-autocomplete v-model="selectedClient"
                           :name="$t('generic.client')"
                           :clearable="false"
                           v-validate="'required|not_empty_object'"
                           @change="onClientChange">
      </client-autocomplete>
    </fg-input>
    <fg-input :label="$t('generic.product')"
              :error="getError($t('generic.product'))"
              required>
      <product-select :options="products"
                      :name="$t('generic.product')"
                      :disabled="!clientSelected"
                      v-model="selectedProduct"
                      v-validate="'required|not_empty_object'"
                      @change="highLightProduct">
      </product-select>
    </fg-input>
    <fg-input :label="$t('generic.jobBoard')"
              :error="getError($t('generic.jobBoard'))"
              required>
      <JobBoardsForProductAutocomplete :disabled="!productSelected"
                                       :product-id="selectedProduct.product_id"
                                       :name="$t('generic.jobBoard')"
                                       :multiple="multipleJobBoards"
                                       array-only
                                       value-key="id"
                                       option-key="job_board"
                                       v-validate="'required'"
                                       v-model="selectedBoards"
                                       @change="onJobBoardSelect">
      </JobBoardsForProductAutocomplete>
    </fg-input>
    <div class="flex items-center">
      <fg-input :label="$t('generic.paymentType')" required
                :error="getError($t('generic.paymentType'))"
                class="flex-1">
        <payment-type-select :contracts-balance="contractsBalance"
                             :disabled="!productSelected"
                             :credits-amount="totalPriceCredits"
                             :credits-balance="creditsBalance"
                             :name="$t('generic.paymentType')"
                             v-model="selectedPayment"
                             v-validate="'required|not_empty_object'"
                             @change="selectPaymentType"
                             @values-change="onPaymentTypesChange">
        </payment-type-select>
      </fg-input>
      <div class="flex mt-3">
        <top-up-credits :client="selectedClient"
                        @credits-add="onCreditsAdd"
                        @contracts-add="onContractsAdd"
        >
        </top-up-credits>
      </div>
    </div>
    <div>
      <fg-input :label="$t('generic.po_number')"
                :value="po_number"
                @input="onPONumberChange"
                :placeholder="$t('generic.po_number')">
      </fg-input>
    </div>
  </div>
</template>
<script>
  import ProductSelect from 'src/components/Autocomplete/ProductSelect'
  import TopUpCredits from './TopUpCredits'
  import JobBoardsForProductAutocomplete from 'src/components/Autocomplete/JobBoardsForProductAutocomplete'
  import ClientAutocomplete from 'src/components/Autocomplete/ClientAutocomplete';
  import PaymentTypeSelect from 'src/components/Autocomplete/PaymentTypeSelect'
  import SegmentService from 'src/services/SegmentService'
  import ClientService from 'src/services/ClientService'
  import JobService from 'src/services/JobService'
  import { mapState } from 'vuex'
  export default {
    name: 'job-create-product',
    components: {
      ProductSelect,
      PaymentTypeSelect,
      JobBoardsForProductAutocomplete,
      ClientAutocomplete,
      TopUpCredits
    },
    props: {
      products: {
        type: Array,
        default: () => []
      },
      job: {
        type: Object,
        default: () => ({})
      },
      product: {
        type: Object,
        default: () => ({})
      },
      client: {
        type: Object,
        default: () => {
          return {
            name: '',
            image: null
          }
        }
      },
      showClientAutocomplete: {
        type: Boolean,
        default: true
      },
      multipleJobBoards: {
        type: Boolean,
        default: true,
      }
    },
    data() {
      let initialClient = this.client && this.client.name ? this.client : {};
      let initialJobBoards = this.job && this.job.job_boards ? this.job.job_boards : []
      let initialProduct = {}
      if (this.job && this.job.product_id) {
        initialProduct = this.products.find(p => p.product_id === this.job.product_id) || {}
      }
      if (initialJobBoards.length) {
        this.onJobBoardSelect(initialJobBoards)
      }
      return {
        selectedProduct: initialProduct,
        selectedPayment: {},
        selectedBoards: initialJobBoards,
        selectedClient: initialClient,
        po_number: '',
        creditsBalance: null,
        allContracts: [],
        allPaymentTypes: [],
        priceData: null,
        loadingCosts: false,
      }
    },
    computed: {
      ...mapState({
        creditPrice: state => state.config.creditPrice
      }),
      totalPrice () {
        return this.priceData ? this.priceData.price : 0
      },
      totalPriceCredits () {
        if (!this.priceData) {
          return 0
        }
        return Math.round(this.priceData.price / this.creditPrice)
      },
      contractsBalance () {
        let contractsFilterFn = (contract) => {
          let boards = this.selectedBoards
          if (!boards) {
            return false
          }
          if (!Array.isArray(boards)) {
            boards = [this.selectedBoards]
          }
          let board = boards.find(board => board.id === contract.job_board_id)
          return board !== undefined
        }
        let validAmounts = this.allContracts.filter(contractsFilterFn)
        return validAmounts.reduce((acc, contract) => acc + contract.amount, 0)
      },
      productSelected() {
        return Object.keys(this.selectedProduct).length > 0
      },
      clientSelected() {
        return Object.keys(this.selectedClient).length > 0
      }
    },
    created() {
      if (this.productSelected) {
        this.highLightProduct(this.selectedProduct)
      }
    },
    methods: {
      highLightProduct(row){
        this.$emit('product-select', row);
        this.calculatePrice(this.selectedBoards)
      },
      onClientChange(val) {
        this.$emit('client-change', val);
      },
      onPONumberChange() {
        this.$emit('po_number-change')
      },
      async onCreditsAdd() {
        await this.getCreditsBalance(this.selectedClient.uuid)
      },
      async onContractsAdd() {
        await this.getContractsBalance(this.selectedClient.uuid)
      },
      async getCreditsBalance (clientId) {
        let {data} = await ClientService.getClientCredits(clientId)
        this.creditsBalance = data.credits
      },
      async getContractsBalance (clientId) {
        let {data} = await ClientService.getClientContractsBalance(clientId)
        this.allContracts = data
        if (!data){
          return
        }
        this.$emit('contracts-balance', data)
      },
      onJobBoardSelect (data) {
        let arr = data
        if (!Array.isArray(data)) {
          arr = [data]
        }
        this.$emit('job-board-select', data)
        this.calculatePrice(data)
        this.getSegmentGroups(data)
      },
      selectPaymentType (row) {
        this.job.payment_type_id = row.id
        this.$emit('payment-selected', row)
      },
      async getSegmentGroups (jobBoards) {
        if (!jobBoards) {
          return
        }
        if (!Array.isArray(jobBoards) && jobBoards) {
          jobBoards = [jobBoards]
        }
        let jobBoardIds = jobBoards.map(j => j.id)
        let {data} = await SegmentService.getJobBoardSegments({job_boards: jobBoardIds})
        this.$emit('segment-change', data)
      },
      onPaymentTypesChange(values) {
        this.allPaymentTypes = values
      },
      async calculatePrice (jobBoards) {
        if (!jobBoards) {
          return
        }

        if (!Array.isArray(jobBoards) && jobBoards) {
          jobBoards = [jobBoards]
        }
        if (!this.allPaymentTypes) {
          // wait until payment times are present
          setTimeout(() => {
            this.calculatePrice(jobBoards)
          }, 500)
          return
        }
        let paymentType = this.allPaymentTypes.find(p => p.type === 'credit')
        if (!paymentType) {
          return
        }
        if (jobBoards.length === 0 || !paymentType.id || !this.client.uuid) {
          this.priceData = null
          return
        }
        let jobBoardIds = jobBoards.map(j => j.id)
        try {
          this.loadingCosts = true
          let {data} = await JobService.getJobPrice({
            client_id: this.client.uuid,
            product_id: this.selectedProduct.product_id,
            payment_type_id: paymentType.id,
            job_boards: jobBoardIds
          })
          this.priceData = data
          if(paymentType.type === 'contract') {
            await this.getContractsBalance(this.client.uuid)
          }
          this.$emit('price-change', {
            price: this.totalPrice,
            credits: this.totalPriceCredits,
            contracts: this.contractsBalance,
            products: data.products
          })
        } finally {
          this.loadingCosts = false
        }
      },
      async validate() {
        return this.$validator.validateAll();
      }
    },
    watch: {
      client: {
        immediate: true,
        handler: async function(newClient) {
          if(!newClient.uuid) {
            return
          }
          await this.getCreditsBalance(newClient.uuid)
          await this.getContractsBalance(newClient.uuid)
          if (newClient.uuid !== this.selectedClient.uuid){
            this.selectedClient = newClient
          }
        }
      },
      product: {
        immediate: true,
        handler: function(newProduct) {
          if (newProduct && Object.keys(newProduct).length) {
            this.selectedProduct = newProduct
          }
        }
      }
    }
  }
</script>
<style scoped>
  .flex-1 {
    flex: 1;
  }
</style>
